'use client'

import React from 'react'
import { X } from 'lucide-react'
import type { ProcessedTheme } from './ProcessedEntry/DomainEntry/processedTypes'
import { watchClasses } from './ProcessedEntry/DomainEntry/processedTypes'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

function splitTokens(raw: string): string[] {
  return raw.split(/[,\s;]+/).map((s) => s.trim()).filter(Boolean)
}

// Gmail-style recipient input, shared by every composer that takes a To/BCC
// list (DomainTakedownWidget, EmailComposeView, and any future one) — each
// address becomes a removable pill instead of sitting inline in one text
// field, so a growing recipient list wraps onto more lines (the field grows
// vertically) instead of becoming an unreadable, unscrollable wall of
// comma-separated text on small screens. Typing a comma/Enter/Tab, or
// pasting a batch of addresses (comma/space/semicolon/newline-separated),
// both commit immediately as pills.
//
// Backing state is a flat string[] — every caller still ultimately sends a
// comma-separated `to`/`bcc` string to the backend, so just `.join(',')`
// this value before building the request body. The backend side of each
// caller also needs to actually treat that joined string as N addresses
// rather than 1 — see the /campaigns/domain/dispatch fix alongside this
// component, which previously only ever sent to the first address.
export function EmailPillInput({
  value,
  onChange,
  theme,
  placeholder,
}: {
  value: string[]
  onChange: (next: string[]) => void
  theme: ProcessedTheme
  placeholder?: string
}) {
  const c = watchClasses(theme)
  const isDark = theme === 'dark'
  const [draft, setDraft] = React.useState('')
  const inputRef = React.useRef<HTMLInputElement>(null)

  const commitDraft = (raw: string) => {
    const tokens = splitTokens(raw)
    if (tokens.length === 0) return
    const merged = [...value]
    for (const t of tokens) if (!merged.includes(t)) merged.push(t)
    onChange(merged)
    setDraft('')
  }

  const removeAt = (idx: number) => onChange(value.filter((_, i) => i !== idx))

  return (
    <div
      onClick={() => inputRef.current?.focus()}
      className={`flex min-h-[30px] flex-wrap items-center gap-1.5 rounded border px-2 py-1 cursor-text bg-card border-border`}
    >
      {value.map((email, idx) => {
        const valid = EMAIL_RE.test(email)
        return (
          <span
            key={`${email}-${idx}`}
            title={valid ? undefined : 'Does not look like a valid email address'}
            className={`flex items-center gap-1 rounded-[4px] border px-2 py-0.5 font-mono text-[10px] ${
              valid
                ? 'border-signal/30 bg-signal-surface text-signal'
                : 'border-warning-border bg-warning-surface text-warning'
            }`}
          >
            {email}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); removeAt(idx) }}
              className="rounded-full hover:opacity-70"
            >
              <X className="h-2.5 w-2.5" />
            </button>
          </span>
        )
      })}
      <input
        ref={inputRef}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ',' || e.key === 'Tab') {
            if (draft.trim()) {
              e.preventDefault()
              commitDraft(draft)
            }
          } else if (e.key === 'Backspace' && draft === '' && value.length > 0) {
            removeAt(value.length - 1)
          }
        }}
        onPaste={(e) => {
          const text = e.clipboardData.getData('text')
          if (/[,\s;]/.test(text.trim())) {
            e.preventDefault()
            commitDraft(text)
          }
        }}
        onBlur={() => { if (draft.trim()) commitDraft(draft) }}
        placeholder={value.length === 0 ? placeholder : undefined}
        className={`min-w-[120px] flex-1 bg-transparent font-mono text-[11px] outline-none ${c.faint} placeholder:text-muted-foreground`}
      />
    </div>
  )
}
